import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { Fragment } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@apps/web/components/ui/tabs';
import { dayLabel } from '@apps/web/features/coverage/model/cell_model';
import { DAY_INDEXES } from '@apps/web/features/coverage/model/coverage_types';
import {
    type CoverageDashboard,
    type CoverageSearchState,
    type CoverageView,
} from '@apps/web/features/coverage/model/use_coverage_dashboard';
import { formatWeekLabel } from '@apps/web/features/coverage/model/week_model';
import { CoverageAreaPane } from '@apps/web/features/coverage/ui/coverage_area_pane';
import { CoverageEmptyState } from '@apps/web/features/coverage/ui/coverage_empty_state';
import { CoverageFilterBar } from '@apps/web/features/coverage/ui/coverage_filter_bar';
import { CoverageGridPane } from '@apps/web/features/coverage/ui/coverage_grid_pane';
import { CoverageKpiStrip } from '@apps/web/features/coverage/ui/coverage_kpi_strip';
import { CoverageLegend } from '@apps/web/features/coverage/ui/coverage_legend';
import { CoverageMapPane } from '@apps/web/features/coverage/ui/coverage_map_pane';

export function CoveragePage({
    dashboard,
    search,
    organizationId,
    clientsLayerEnabled,
}: {
    readonly dashboard: CoverageDashboard;
    readonly search: CoverageSearchState;
    readonly organizationId: string | null;
    readonly clientsLayerEnabled: boolean;
}) {
    const dayLabels = DAY_INDEXES.map((dayIndex) =>
        dayLabel(dayIndex, dashboard.workWeekStartDay, 'full'),
    );

    const emptyState = !dashboard.hasAnyCaregivers
        ? 'no-caregivers'
        : !dashboard.hasAnyAvailabilityData && !dashboard.filtersActive
          ? 'no-availability-data'
          : 'no-matches';

    if (dashboard.hasError) {
        // A failed fetch (for example a shared link whose week no longer
        // matches the org's work week) must not read as an empty org.
        return (
            <div className="space-y-3 p-4 text-center lg:p-6">
                <h1 className="text-xl font-semibold">Coverage</h1>
                <p className="text-muted-foreground text-sm">
                    We couldn’t load this view. The selected week may not match
                    the organization’s work week.
                </p>
                <div className="flex justify-center gap-2">
                    <Button variant="outline" onClick={dashboard.retry}>
                        Try again
                    </Button>
                    {search.week ? (
                        <Button onClick={dashboard.goToCurrentWeek}>
                            Go to current week
                        </Button>
                    ) : null}
                </div>
            </div>
        );
    }

    return (
        // ph-no-capture: rows are caregiver PII (names, cities, ZIPs);
        // keep them out of PostHog DOM autocapture.
        <div className="ph-no-capture space-y-4 p-4 lg:p-6">
            <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-xl font-semibold">Coverage</h1>
                <div className="grow" />
                <div className="border-border flex items-center rounded-lg border">
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Previous week"
                        disabled={dashboard.isLoading}
                        onClick={() => dashboard.goToWeek(-1)}
                    >
                        <ChevronLeft className="size-4" />
                    </Button>
                    <span className="min-w-40 text-center text-sm font-medium tabular-nums">
                        {formatWeekLabel(
                            dashboard.weekStart,
                            dashboard.weekEnd,
                        )}
                    </span>
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Next week"
                        disabled={dashboard.isLoading}
                        onClick={() => dashboard.goToWeek(1)}
                    >
                        <ChevronRight className="size-4" />
                    </Button>
                </div>
                {search.week ? (
                    <Button
                        variant="outline"
                        onClick={dashboard.goToCurrentWeek}
                    >
                        This week
                    </Button>
                ) : null}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild={true}>
                        <Button
                            disabled={
                                dashboard.rows.length === 0 ||
                                dashboard.isLoading ||
                                dashboard.exportStatus === 'pending'
                            }
                        >
                            <Download className="size-4" />
                            {dashboard.exportStatus === 'pending'
                                ? 'Exporting…'
                                : 'Export'}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem
                            onClick={() => void dashboard.exportCsv()}
                        >
                            Download CSV
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => {
                                void dashboard.exportXlsx();
                            }}
                        >
                            Download Excel
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            {dashboard.isLoading ? (
                <div
                    className="space-y-3"
                    aria-label="Loading coverage"
                    aria-live="polite"
                    role="status"
                >
                    <span className="sr-only">Loading coverage</span>
                    <Skeleton className="h-24 w-full rounded-xl" />
                    <Skeleton className="h-12 w-full rounded-lg" />
                    <Skeleton className="h-96 w-full rounded-xl" />
                </div>
            ) : (
                <Fragment>
                    {dashboard.refreshErrorMessage ? (
                        <div
                            className="border-border bg-muted/50 flex items-center gap-2 rounded-lg border px-3 py-2"
                            role="alert"
                        >
                            <p className="text-muted-foreground grow text-sm">
                                {dashboard.refreshErrorMessage}
                            </p>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={dashboard.retry}
                            >
                                Try again
                            </Button>
                        </div>
                    ) : null}
                    {dashboard.exportStatus === 'error' ? (
                        <div
                            className="border-destructive/40 bg-destructive/5 flex items-center gap-2 rounded-lg border px-3 py-2"
                            role="alert"
                        >
                            <p className="text-destructive grow text-sm">
                                {dashboard.exportError ??
                                    'We couldn’t create the export.'}
                            </p>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={dashboard.dismissExportError}
                            >
                                Dismiss
                            </Button>
                        </div>
                    ) : null}
                    <CoverageKpiStrip
                        kpis={dashboard.kpis}
                        unknownOnlyActive={
                            search.availability === 'unknown_only'
                        }
                        onToggleUnknownOnly={dashboard.toggleUnknownOnly}
                    />
                    <CoverageLegend />
                    <Tabs
                        value={search.view}
                        onValueChange={(value) =>
                            dashboard.setView(value as CoverageView)
                        }
                    >
                        <TabsList>
                            <TabsTrigger value="grid">Grid</TabsTrigger>
                            <TabsTrigger value="coverage">Coverage</TabsTrigger>
                            <TabsTrigger value="map">Map</TabsTrigger>
                        </TabsList>
                        <div className="mt-3 space-y-3">
                            <CoverageFilterBar
                                filters={search}
                                facetOptions={dashboard.facetOptions}
                                dayLabels={dayLabels}
                                filtersActive={dashboard.filtersActive}
                                clearEpoch={dashboard.clearEpoch}
                                includeInactive={search.includeInactive}
                                onIncludeInactiveChange={
                                    dashboard.setIncludeInactive
                                }
                                onChange={dashboard.setFilters}
                                onClear={dashboard.clearFilters}
                            />
                            <TabsContent value="grid">
                                <CoverageGridPane
                                    key={dashboard.rowsResetKey}
                                    rows={dashboard.rows}
                                    workWeekStartDay={
                                        dashboard.workWeekStartDay
                                    }
                                    isLoading={dashboard.isLoading}
                                    organizationId={organizationId}
                                    renderEmptyState={() => (
                                        <CoverageEmptyState
                                            variant={emptyState}
                                            onClearFilters={
                                                emptyState === 'no-matches'
                                                    ? dashboard.clearFilters
                                                    : undefined
                                            }
                                        />
                                    )}
                                />
                            </TabsContent>
                            <TabsContent value="coverage">
                                <CoverageAreaPane
                                    coverage={dashboard.coverage}
                                    area={search.area}
                                    onAreaChange={dashboard.setArea}
                                    onSelectCell={dashboard.selectCoverageCell}
                                    onClearFilters={
                                        dashboard.filtersActive
                                            ? dashboard.clearFilters
                                            : undefined
                                    }
                                />
                            </TabsContent>
                            <TabsContent value="map">
                                {search.view === 'map' ? (
                                    <CoverageMapPane
                                        status={dashboard.mapStatus}
                                        errorMessage={dashboard.mapErrorMessage}
                                        refreshErrorMessage={
                                            dashboard.mapRefreshErrorMessage
                                        }
                                        onRetry={dashboard.retryMap}
                                        points={dashboard.mapPoints}
                                        clientDensityCells={
                                            dashboard.mapClientDensityCells
                                        }
                                        clientDensityStatus={
                                            dashboard.mapClientDensityStatus
                                        }
                                        clientDensityErrorMessage={
                                            dashboard.mapClientDensityErrorMessage
                                        }
                                        clientsLayerEnabled={
                                            clientsLayerEnabled
                                        }
                                        plotClients={search.plotClients}
                                        onPlotClientsChange={
                                            dashboard.setPlotClients
                                        }
                                        organizationId={organizationId}
                                        visibleCaregiverIds={
                                            dashboard.visibleCaregiverIds
                                        }
                                        coverageBanner={
                                            dashboard.mapCoverageBanner
                                        }
                                        canRequestGeocode={
                                            dashboard.canRequestGeocode
                                        }
                                        geocodeStatus={dashboard.geocodeStatus}
                                        geocodeError={dashboard.geocodeError}
                                        onRequestGeocode={
                                            dashboard.requestGeocode
                                        }
                                        onDismissGeocodeError={
                                            dashboard.dismissGeocodeError
                                        }
                                    />
                                ) : null}
                            </TabsContent>
                        </div>
                    </Tabs>
                </Fragment>
            )}
        </div>
    );
}
